import { CHANNEL_KIND } from '../catalog/channel';
import type { ChannelRef } from '../catalog/channel';
import type { ConfigIssue } from '../catalog/issue';
import { ISSUE, ISSUE_TARGET, configIssue } from '../catalog/issue';
import { DALI_BRIDGE_ADDRESS } from '../catalog/dali';
import { RCU_LIMITS } from '../catalog/limits';
import { LOAD_TYPE_SPECS } from '../catalog/loadType';
import { RAIL_TYPE_KEYS, RAIL_TYPE_SPECS } from '../catalog/railType';
import type { RcuConfig, WiredLoad } from '../model';
import { hasTarget } from '../model';
import { moduleCount } from '../query/capacity';
import { unwiredLoads, wiredLoads } from '../query/grouping';
import { channelKey, findRail, findRoom, railsOfType } from '../query/lookup';
import { describeRef, isChannelInRange, targetFromRef } from '../query/targets';

/**
 * One rule, one concern. Each is a pure function of the configuration and is
 * unit-tested on its own; `VALIDATION_RULES` composes them.
 */
export type ValidationRule = (config: RcuConfig) => readonly ConfigIssue[];

/** Wired loads grouped by the output they sit on. */
const byChannel = (config: RcuConfig): ReadonlyMap<string, readonly WiredLoad[]> => {
  const groups = new Map<string, WiredLoad[]>();
  for (const load of wiredLoads(config)) {
    const key = channelKey(load.channel);
    groups.set(key, [...(groups.get(key) ?? []), load]);
  }
  return groups;
};

const outputCountInRange: ValidationRule = (config) => {
  const { outputCount } = config.rcu;
  const outOfRange =
    outputCount < RCU_LIMITS.MIN_OUTPUTS || outputCount > RCU_LIMITS.MAX_OUTPUTS;
  return outOfRange
    ? [
        configIssue(
          ISSUE.OUTPUT_COUNT_OUT_OF_RANGE,
          { kind: ISSUE_TARGET.RCU },
          String(outputCount),
        ),
      ]
    : [];
};

const railAddressInRange: ValidationRule = (config) =>
  config.rails
    .filter(
      (rail) =>
        rail.address < RCU_LIMITS.MIN_RAIL_ADDRESS || rail.address > RCU_LIMITS.MAX_RAIL_ADDRESS,
    )
    .map((rail) =>
      configIssue(
        ISSUE.RAIL_ADDRESS_OUT_OF_RANGE,
        { kind: ISSUE_TARGET.RAIL, id: rail.id },
        String(rail.address),
      ),
    );

const railAddressUniquePerType: ValidationRule = (config) =>
  RAIL_TYPE_KEYS.flatMap((type) => {
    const addressCounts = new Map<number, number>();
    for (const rail of railsOfType(config, type)) {
      addressCounts.set(rail.address, (addressCounts.get(rail.address) ?? 0) + 1);
    }
    return [...addressCounts.entries()]
      .filter(([, count]) => count > 1)
      .map(([address]) =>
        configIssue(
          ISSUE.RAIL_ADDRESS_CONFLICT,
          { kind: ISSUE_TARGET.RAIL },
          `${RAIL_TYPE_SPECS[type].shortLabel} address ${address}`,
        ),
      );
  });

const railTypeLimit: ValidationRule = (config) =>
  RAIL_TYPE_KEYS.flatMap((type) =>
    railsOfType(config, type).length > RCU_LIMITS.MAX_RAILS_PER_TYPE
      ? [
          configIssue(
            ISSUE.RAIL_TYPE_LIMIT,
            { kind: ISSUE_TARGET.RAIL },
            RAIL_TYPE_SPECS[type].shortLabel,
          ),
        ]
      : [],
  );

/** Rails and DALI bridges come out of one budget on the device. */
const moduleTotalLimit: ValidationRule = (config) =>
  moduleCount(config) > RCU_LIMITS.MAX_MODULES_TOTAL
    ? [
        configIssue(
          ISSUE.MODULE_TOTAL_LIMIT,
          { kind: ISSUE_TARGET.RCU },
          String(moduleCount(config)),
        ),
      ]
    : [];

const bridgeAddressInRange: ValidationRule = (config) =>
  config.daliBridges
    .filter(
      (bridge) =>
        bridge.address < DALI_BRIDGE_ADDRESS.MIN || bridge.address > DALI_BRIDGE_ADDRESS.MAX,
    )
    .map((bridge) =>
      configIssue(
        ISSUE.DALI_BRIDGE_ADDRESS_CONFLICT,
        { kind: ISSUE_TARGET.BRIDGE, id: bridge.id },
        String(bridge.address),
      ),
    );

const bridgeMasterUnique: ValidationRule = (config) =>
  [...config.daliBridges.reduce((counts, bridge) => {
    counts.set(bridge.master, (counts.get(bridge.master) ?? 0) + 1);
    return counts;
  }, new Map<number, number>())]
    .filter(([, count]) => count > 1)
    .map(([master]) =>
      configIssue(
        ISSUE.DALI_BRIDGE_MASTER_CONFLICT,
        { kind: ISSUE_TARGET.BRIDGE },
        String(master),
      ),
    );

/** Every wired load must point at a rail that exists and a channel that exists. */
const wiringReachable: ValidationRule = (config) =>
  wiredLoads(config).flatMap((load) => {
    const issues: ConfigIssue[] = [];
    const target = { kind: ISSUE_TARGET.LOAD, id: load.id } as const;
    if (load.channel.kind === CHANNEL_KIND.DIN && findRail(config, load.channel.railId) === undefined) {
      issues.push(configIssue(ISSUE.UNKNOWN_RAIL, target, `${load.name} → ${load.channel.railId}`));
    } else if (!isChannelInRange(config, load.channel)) {
      issues.push(
        configIssue(ISSUE.CHANNEL_OUT_OF_RANGE, target, `${load.name} → ${describeRef(config, load.channel)}`),
      );
    }
    return issues;
  });

const noChannelReused: ValidationRule = (config) =>
  [...byChannel(config).values()]
    .filter((loads) => loads.length > 1)
    .flatMap((loads) => {
      const ref: ChannelRef | undefined = loads[0]?.channel;
      const names = loads.map((load) => load.name).join(', ');
      return ref === undefined
        ? []
        : [
            configIssue(
              ISSUE.DUPLICATE_CHANNEL,
              { kind: ISSUE_TARGET.LOAD, id: loads[0]?.id },
              `${describeRef(config, ref)} (${names})`,
            ),
          ];
    });

const loadTypeMatchesOutput: ValidationRule = (config) =>
  wiredLoads(config).flatMap((load) => {
    const target = targetFromRef(config, load.channel);
    if (target === null || target.acceptedLoadTypes.includes(load.type)) return [];
    return [
      configIssue(
        ISSUE.INCOMPATIBLE_LOAD,
        { kind: ISSUE_TARGET.LOAD, id: load.id },
        `${load.name} (${LOAD_TYPE_SPECS[load.type].label}) → ${target.shortLabel} (${target.outputHint} only)`,
      ),
    ];
  });

const everyLoadIsWired: ValidationRule = (config) =>
  unwiredLoads(config).map((load) =>
    configIssue(ISSUE.UNWIRED_LOAD, { kind: ISSUE_TARGET.LOAD, id: load.id }, load.name),
  );

const everyLoadsRoomExists: ValidationRule = (config) =>
  config.loads
    .filter((load) => load.roomId !== null && findRoom(config, load.roomId) === undefined)
    .map((load) =>
      configIssue(ISSUE.ORPHAN_ROOM, { kind: ISSUE_TARGET.LOAD, id: load.id }, load.name),
    );

/** Only useful once something is wired: rooms are what the controls page groups by. */
const roomsDefinedOnceWired: ValidationRule = (config) =>
  config.rooms.length === 0 && wiredLoads(config).length > 0
    ? [configIssue(ISSUE.NO_ROOMS, { kind: ISSUE_TARGET.ROOM })]
    : [];

const notEmpty: ValidationRule = (config) =>
  config.rails.length === 0 && config.loads.length === 0
    ? [configIssue(ISSUE.EMPTY_CONFIG, { kind: ISSUE_TARGET.RCU })]
    : [];

/** A tablet with nowhere to send commands cannot drive anything. */
const reachableAddress: ValidationRule = (config) =>
  hasTarget(config.rcu) ? [] : [configIssue(ISSUE.NO_RCU_ADDRESS, { kind: ISSUE_TARGET.RCU })];

export const VALIDATION_RULES: readonly ValidationRule[] = [
  outputCountInRange,
  railAddressInRange,
  railAddressUniquePerType,
  railTypeLimit,
  moduleTotalLimit,
  bridgeAddressInRange,
  bridgeMasterUnique,
  wiringReachable,
  noChannelReused,
  loadTypeMatchesOutput,
  everyLoadIsWired,
  everyLoadsRoomExists,
  roomsDefinedOnceWired,
  reachableAddress,
  notEmpty,
];
